import { ActionCreator, Dispatch, Action, Store } from "redux";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import { RootState } from "./reducers";
import { TokenService } from "./libs/tokenService";
import { Translator } from "libs";

export interface ICustomWindow extends Window {
	store?: Store<RootState>;
	_?: Translator["transChoice"];
	gapi?: any;
	loadLang?: any;
	intlTelInputGlobals: any;
	tokenService?: TokenService;
	accessToken?: string;
}

export enum FlashMessageType {
	SUCCESS = "success",
	ERROR = "error",
}

export interface FlashMessage {
	id: number;
	text: string;
	type: FlashMessageType;
	duration: number;
}

export interface TokenResponse {
	access_token: string;
	expires_in: number;
	token_type: string;
	scope: null;
	refresh_token: string;
}

export interface APIError {
	error: string;
	error_description: string;
}

export interface ClubResponse {
	responseCode: string;
	data: Club;
}

export interface Club {
	clubInfo: ClubInfo;
	clubDetails: ClubDetails;
	courts: Court[];
}

export interface ClubDetails {
	type: string;
	memberStatus: string;
	hasSeasons: boolean;
	isVerified: boolean;
	isFavorite: boolean;
	hasOnlinePayment: boolean;
	openingHours: OpeningHour[];
	guestsGenerallyAllowed: boolean;
	clubPerks: ClubPerk[];
	photos: Photo[];
	maxDaysInAdvance: number;
}

export interface ClubPerk {
	perkId: number;
	name: string;
	hasAmount: boolean;
	amount?: number;
}

export interface OpeningHour {
	dayOfWeek: number;
	opening: number;
	closing: number;
}

export interface Photo {
	photoId: number;
	path: string;
	default: boolean;
}

export interface ClubInfo {
	clubId: string;
	name: string;
	landLinePhone: string;
	mobilePhone: string;
	email: string;
	contractedClub: boolean;
	canonicalName: string;
	timeZone: string;
	address1: string;
	city: string;
	country: string;
	zipCode: string;
	photoUrl: string;
	longitude: string;
	latitude: string;
	aboutText: string;
	termsText: string;
	web: string;
	flow: string;
	migratedURL?: string;
}

export interface Court {
	courtId: string;
	sport: string;
	surfaceType: string;
	courtType: string;
	name: string;
	ballMachine: boolean;
	order: number;
}

export interface Weather {
	id: number;
	type: string;
	min: number;
	max: number;
	icon: string;
	description: string;
}

export interface SlotsResponse {
	responseCode: string;
	data: SlotsData;
}

export interface SlotsData {
	flow: string;
	courts: Court[];
}

export interface Court {
	courtId: string;
	sport: string;
	surfaceType: string;
	courtType: string;
	name: string;
	ballMachine: boolean;
	order: number;
	slots: Slot[];
	bookingInterval: number;
}

export interface RatingActivity {
	actor: ActivityActor;
	foreign_id: string;
	id: string;
	object: string;
	origin: string;
	rating: FederationRating;
	target: string;
	time: Date;
	verb: string;
}

export interface FederationRating {
	userId: string;
	classificationValue: string;
	classificationCategory: string;
	ratingNumber: string;
	lastClassificationCategory: string;
	lastRatingNumber: string;
	ratingChange: RatingChange;
	bestClassificationCategory: string;
	bestRatingNumber: string;
	ageCategory: string;
	ratingType: string;
	licenseeSince: string;
	licenseNumber: string;
	licenseeStatus: string;
	federationCode: FederationCode;
	federationName: string;
	lastUpdatedFederation: string;
}

export enum FederationCode {
	SWISS_TENNIS = "st",
}

export enum RatingChange {
	up = "up",
	down = "down",
	none = "none",
}

export interface Slot {
	startDateTime: number;
	endDateTime: number;
	slotType: SlotType;
	slotContext: SlotContext;
}

export interface SlotContext {
	notAvailable?: NotAvailable;
	available?: Available;
	booking?: Booking;
	blocking?: Blocking;
}

export interface Available {
	durations: number[];
}

export interface NotAvailable {
	code: null;
	reason: Reason;
}

export interface Booking {
	reservationId: string;
	owner: string;
	ownerId: null | string;
	players: Player[];
	info: null | string;
}

export interface Blocking {
	blockingType: string;
	longDescription: string;
	shortDescription: string;
}

export enum Reason {
	ReservationsCannotBeMadeInThePast = "Reservations cannot be made in the past.",
}

export enum SlotType {
	Available = "available",
	NotAvailable = "notAvailable",
	Booked = "booked",
	Blocked = "blocked",
}

export enum PaymentMethod {
	CreditCard = "creditcard",
	Balance = "credit_balance",
	OnSite = "onsite",
	GCWallet = "gc_wallet",
}

export enum BookingOwner {
	Me = "mine",
	Buddy = "buddy",
	Player = "player",
	Club = "club",
}

export interface ChampionshipOptInContext {
	currency: string;
	totalCost: number;
	vouchers: Voucher[];
	promotions?: Promotion[];
	trial?: ChampionshipTrial;
	paymentOptions?: PaymentOption[];
}

export interface ReservationContext {
	currency: string;
	totalCost: number;
	totalCostRange: TotalCostRange;
	canCancelUntil: number;
	canCancel: boolean;
	partnerNamesRequired: boolean;
	guestsAllowed: boolean;
	mustPayOnline: boolean;
	costPositions: CostPosition[];
	rules: Rule[];
	paymentOptions: PaymentOption[];
	championships: Championship[];
	vouchers: Voucher[];
	requiredValidation?: Array<ProfileValidationFlag>;
}

export enum ProfileValidationFlag {
	ContactDetails = "contactDetails",
}

export enum ChampionshipType {
	Championship = "championship",
	Tournament = "tournament",
}

export interface Championship {
	available: boolean;
	optedIn: boolean;
	optedInAt: number;
	notAvailableReason: string;
	uuid: string;
	name: string;
	description: null;
	type: string;
	matchesPlayed?: number;
	winRate?: number;
	owner: string;
	ownerUUId: string;
	ranking: string;
	sport: string;
	createdAt: number;
	startDateTime: number;
	endDateTime: number;
	settings: ChampionshipSettings;
	players: number;
	league?: ChampionshipPlayerLeague;
	rank?: Rank;
	challenges?: Array<ChampionshipChallenge>;
	winPoints?: number;
	championshipType: ChampionshipType;
	linkedChampionship?: string;
}

export interface ChampionshipSettings {
	city: null | string;
	club: null | any;
	startingPoints: number;
	readMoreUrl: string;
	embeddedReadMoreUrl: string;
	pictureUrl: string | null;
	currency: null | string;
	optInFee: number;
	showReadMoreFirst: boolean;
	showInvite?: boolean;
	leagues?: Array<ChampionshipLeague>;
	provisionalRating?: ChampionshipProvisionalRating;
	preselectDefault: boolean;
	trial?: ChampionshipTrial;
	maxParticipants?: number;
	optInDeadline?: null | number;
}

export interface ChampionshipTrial {
	canStartTrial: boolean;
	hasActiveTrial: boolean;
	hasExpiredTrial: boolean;
	trialExpiresAt: number;
	trialMaxMatches: number;
	expiryInformationText?: string;
	upgradePriceInformationText?: string;
	cardForTrial: boolean;
	autoChargeAfterTrial: boolean;
}

export interface ChampionshipLeague {
	colors: ChampionshipLeagueColor;
	id: string;
	minPoints: number;
	name: string;
	qualifiers: number;
	url: string;
}

export interface ChampionshipChallenge {
	id: string;
	label: string;
	type: string;
	description: string;
	reward?: ChampionshipChallengeReward;
	timed?: ChampionshipTimedChallenge;
	trialEligible?: boolean;
	values: ChampionshipChallengeValue;
	complete: boolean;
	rankable?: boolean;
	rank?: number;
}

export interface ChampionshipChallengeValue {
	currentValue: number;
	minvalue: number;
	maxValue: number;
}

export interface ChampionshipChallengeReward {
	rewardLabel: string;
	rewardText: string;
	rewardUrl: string;
	iconUrl: string;
}

export interface ChampionshipTimedChallenge {
	fromPeriod: number;
	toPeriod: number;
}

export interface ChampionshipPlayerLeague {
	id: string;
	ranking: number;
	players: number;
}

export interface ChampionshipLeagueColor {
	gradientStart: string;
	gradientEnd: string;
	background: string;
}

export interface ChampionshipProvisionalRating {
	minMatches: number;
	matchesPlayed: number;
	completed: boolean;
}

export interface CostPosition {
	type: string;
	name: string;
	cost: string;
}

export interface PaymentOption {
	paymentMethod: string;
	notAvailableReason: string;
	note: string;
	available: boolean;
	currency: null;
}

export interface Rule {
	duration: number;
	minPartners: number;
	maxPartners: number;
}

export interface TotalCostRange {
	from: null;
	to: null;
}

export interface Voucher {
	valid: null;
	discount: null;
	invalidReason: string;
	available: boolean;
	notAvailableReason: string;
	discountCurrency: string;
	voucherCode: null;
}

export interface Promotion {
	discount: number;
	discountCurrency: string;
	promotionId: string;
	promotionTitle: string;
	validUntil?: number;
}

export interface StripeTokenResponse {
	id: string;
	object: string;
	card: StripeCard;
	client_ip: string;
	created: number;
	livemode: boolean;
	type: string;
	used: boolean;
}

export interface StripeCard {
	id: string;
	object: string;
	address_city: null;
	address_country: null;
	address_line1: null;
	address_line1_check: null;
	address_line2: null;
	address_state: null;
	address_zip: string;
	address_zip_check: string;
	brand: string;
	country: string;
	cvc_check: string;
	dynamic_last4: null;
	exp_month: number;
	exp_year: number;
	funding: string;
	last4: string;
	metadata: Metadata;
	name: null;
	tokenization_method: null;
}

export interface Metadata {}

export interface AddCardResponse {
	responseCode: string;
	data: Card[];
}

export interface Card {
	cardId: string;
	last4: string;
	expMonth: number;
	expYear: number;
	brand: string;
	country: string;
	default: boolean;
	networks?: CardNetwork;
}

export interface CardNetwork {
	available?: Array<string>;
	preferred?: string;
}

export interface Reservation {
	reservationId: string;
	startDateTime: number;
	endDateTime: number;
	coachFlag: string;
	ballMachine: string;
	owner: string;
	ownerId: string;
	court: Court;
	club: Club;
	players: Player[];
	paymentDetails: PaymentDetails;
	conditions: Conditions;
	championship: null;
	additionalInfos: any[];
	flow: string;
}

export interface Conditions {
	cancelPossible: boolean;
	cancelUntil: number;
}

export interface PaymentDetails {
	paymentMethod: string;
	paymentStatus: string;
	currency: string;
	paid: boolean;
	totalCost: number;
	costPositions: any[];
	paymentCardInfo: PaymentCardInfo;
	voucherCode: string;
	networks?: CardNetwork;
}

export interface PaymentCardInfo {
	cardId: string;
	last4: string;
	brand: string;
}

export interface BaseUser {
	userId: string;
	firstName: string;
	lastName: string;
	photoUrl?: string;
}

export interface Player {
	playerId?: string;
	userId?: string;
	type?: string;
	firstName?: string;
	lastName?: string;
	fullName?: string;
	photoUrl?: string;
	rating?: Rating;
	leagueId?: string;
	winPoints?: number;
	championships?: Championship[];
}

export interface User extends Player, BaseUser {
	type?: string;
	userId: string;
	photoUrl: string;
	firstName: string;
	lastName: string;
	city: string;
	country: string;
	coachType?: string;
	userRelationship: string;
	numberOfFriends: number;
	age?: null | string;
	gender?: null | string;
	sport?: null | string;
}

export interface Friend extends BaseUser {
	city: string;
	country: string;
	userRelationship: string;
	numberOfFriends: number;
	coachType: null | string;
	rating: Rating;
}

export enum Flag {
	ShowOnboarding = "showOnboarding",
	MustAcceptTOC = "mustAcceptTOC",
	MustAlterPassword = "mustAlterPassword",
}

interface NotificationSetting {
	email: boolean;
	push: boolean;
}

export enum PrivacySettingValues {
	CLUB_AND_BUDDIES = "clubAndBuddies",
	BUDDIES = "buddies",
	PRIVATE = "private",
}

export enum PrivacySettingKeys {
	RESERVATIONS = "reservations",
	GAMES = "games",
}

export interface NotificationSettingsArray extends Array<{ name: string; email: boolean; push: boolean }> {}

export interface ProfileFlags {
	[Flag.ShowOnboarding]: 0 | 1;
	[Flag.MustAcceptTOC]: 0 | 1;
	[Flag.MustAlterPassword]: 0 | 1;
}

export interface Profile extends User {
	email: string;
	emailValid: boolean;
	numberOfPendingRequests: number;
	mobilePhone: string;
	mobileValid: boolean;
	address1: null;
	zipCode: string;
	coachType?: string;
	clubRelations: ClubRelation[];
	showOnboarding: number;
	dob: null;
	useNetwork: boolean;
	aboutMe: null;
	favorites: Club[];
	latitude: string;
	longitude: string;
	language: string;
	profileManagedBy?: string[];
	flags: ProfileFlags;
	notificationSettings: {
		[key: string]: NotificationSetting;
	};
	privacySettings: Record<PrivacySettingKeys, PrivacySettingValues>;
	timeZone: string;
	locationValid: boolean;
}

export interface SocialFindResponse {
	responseCode: string;
	pageRows: number;
	pageLimit: number;
	pageCurrent: number;
	data: User[];
}

export interface ChampionshipInvitePayload {
	shareUrl?: string;
	players: InvitePlayer[];
}

export interface InvitePlayer extends Player {
	inviteStatus?: ChampionshipInviteStatus;
}

export enum ChampionshipInviteStatus {
	INVITED = "invited",
	JOINED = "joined",
}

export interface NationalRating {
	ratingCode: string;
	ITN: string;
	countryCode: string;
	ratings: NationalRatingRating[];
}

export interface Ratings {
	nationalRatings: NationalRating[];
	ITN: {
		ratings: ITNRating[];
	};
	reference: {
		description: string;
		link: string;
	};
}

export interface NationalRatingRating {
	ratingCode: string;
	ITN: string;
}

export interface Rating {
	country: null | string;
	ratingValue: null | string;
	ITN: null | string;
	ITNName: null | string;
	officialRating?: OfficialRating;
}

export interface OfficialRating {
	code?: string;
	name?: string;
}

export interface ITNRating {
	ratingCode: string;
	ratingDescription: string;
	ratingName: string;
}

export interface ClubRelation {
	clubId: number;
	categoryId: number;
	membership: boolean;
	clubUUId: string;
}

export interface ReservationsResponse {
	responseCode: string;
	pageRows: number;
	pageLimit: number;
	pageCurrent: number;
	data: ReservationsResponseData;
}

export interface ReservationsResponseData {
	reservations: Reservation[];
}

export interface Reservation {
	reservationId: string;
	startDateTime: number;
	endDateTime: number;
	coachFlag: string;
	ballMachine: string;
	owner: string;
	ownerId: string;
	court: Court;
	club: Club;
	players: Player[];
	paymentDetails: PaymentDetails;
	conditions: Conditions;
	championship: null;
	additionalInfos: any[];
	flow: string;
}

export interface ClubInfo {
	clubId: string;
	name: string;
	landLinePhone: string;
	mobilePhone: string;
	email: string;
	contractedClub: boolean;
	canonicalName: string;
	timeZone: string;
	address1: string;
	city: string;
	country: string;
	zipCode: string;
	photoUrl: string;
	longitude: string;
	latitude: string;
	aboutText: string;
	termsText: string;
	web: string;
	flow: string;
}

export interface Conditions {
	cancelPossible: boolean;
	cancelUntil: number;
}

export interface Court {
	sport: string;
	surface: string;
	name: string;
	icon: string;
}

export interface SearchResult {
	clubId: string;
	clubName: string;
	memberStatus: string;
	photoUrl: string;
	type: string | null;
	timeZone: string;
	contractedClub: boolean;
	favorite: boolean;
	longitude: string;
	latitude: string;
	range: number;
	availableSlots: AvailableSlot[];
	address1: string;
	city: string;
	zipCode: null;
	canonicalName: string;
	flow: string;
	nearestSlot: null | AvailableSlot;
	searchTimeStamp: number;
	className: string;
}

export interface AvailableSlot {
	startDateTime: number;
	numberOfCourts: number;
}

export interface Stat {
	name: string;
	type: string;
	simple: SimpleStat[] | null;
	references: any[] | null;
	data?: any[];
}

export interface SimpleStat {
	absolute: number;
	relative: number;
	name: string;
}

export interface Location {
	name: string;
	lat: number;
	lng: number;
	playersCount?: number;
	placeId: string;
	errorCode?: number;
}

export interface PhotoDimensions {
	height: number;
	width: number;
}

export interface GcPost {
	language: string;
	link: string;
	linkName: string;
	photoUrl: string;
	photoDimensions: PhotoDimensions;
	text: string;
	title: string;
}

export interface ClubPost {
	language: string;
	link: string;
	linkName: string;
	photoUrl: string;
	photoDimensions: PhotoDimensions;
	text: string;
	title: string;
}

export interface ClubPostActivity {
	actor: ActivityClubActor;
	foreign_id: string;
	id: string;
	gcPost: GcPost;
	origin: string;
	target: string;
	time: Date;
	verb: string;
}

export interface ReservationActivity {
	actor: ActivityActor;
	foreign_id: string;
	id: string;
	object: string;
	origin: string;
	reservation: Reservation;
	target: string;
	time: Date;
	verb: string;
}

export interface GcPostActivity {
	actor: ActivityActor | string;
	foreign_id: string;
	id: string;
	gcPost: GcPost;
	federation?: string;
	origin: string;
	target: string;
	time: Date;
	verb: string;
}

export interface ActivityActor {
	created_at: Date;
	updated_at: Date;
	id: string;
	data: ActivityActorData;
}

export interface ActivityClubActor {
	created_at: Date;
	updated_at: Date;
	id: string;
	data: ActivityClubActorData;
}

export interface ActivityActorData {
	createdAt: string;
	firstName: string;
	lastName: string;
	latitude: string;
	locale: string;
	longitude: string;
	photoUrl: string;
	userId: string;
	username: string;
}

export interface ActivityClubActorData {
	createdAt: string;
	name: string;
	latitude: string;
	locale: string;
	longitude: string;
	photoUrl: string;
	clubId: string;
	username: string;
}

export interface ReservationActivity {
	adminFlag: boolean;
	championship: Championship;
	club: Club;
	court: Court;
	endDateTime: number;
	owner: ActivityOwner;
	ownerId: string;
	players: any[];
	reservationId: string;
	startDateTime: number;
}

export interface ActivityOwner {
	createdAt: number;
	firstName: string;
	id: string;
	lastName: string;
	latitude: string;
	locale: string;
	longitude: string;
	networkPropertyChange: boolean;
	photoUrl: string;
	rating: null;
	timeZone: string;
	useNetwork: boolean;
	userId: string;
	username: string;
}

export interface AdData {
	feedFrequency: number;
	ads: Ad[];
}

export interface Ad {
	name: string;
	title: string;
	brandName: string;
	brandUrl: string;
	content: null | string;
	richContent: null | string;
	picture: null | string;
	mainPicture: null | string;
	link: null | string;
	linkName: null | string;
	embedded: null;
	campaign: string;
	featured: boolean;
	offer_type: string;
	offer: string;
	date: number;
	order?: number;
	location: string[];
	resources: string[];
}

export enum AdLocation {
	All = "all",
	NewsStream = "news_stream",
	DashboardSidebar = "dashboard_sidebar",
	ReservationSidebar = "reservation_sidebar",
	DealsPage = "deals_page",
	ChampionshipSidebar = "championship_sidebar",
}

export interface StreamException {
	detail: string;
	status_code: number;
	code: number;
	exception: string;
	duration: string;
}

export interface BuddyAcceptedActivity {
	actor: Actor;
	foreign_id: string;
	friend?: Friend;
	id: string;
	object: string;
	origin: string;
	target: string;
	time: Date;
	verb: string;
	reservation?: Reservation;
}

export interface Actor {
	created_at: Date;
	updated_at: Date;
	id: string;
	data: OwnerClass;
}

export interface OwnerClass {
	createdAt: number;
	firstName: string;
	id: string;
	lastName: string;
	latitude: string;
	locale: string;
	longitude: string;
	networkPropertyChange: boolean;
	photoUrl: string;
	rating: Rating;
	timeZone: string;
	useNetwork: boolean;
	userId: string;
	username: null | string;
}

export interface Friend {
	created_at: Date;
	updated_at: Date;
	id: string;
	data: FriendData;
}

export interface FriendData {
	createdAt: number | string;
	firstname?: string;
	lastName: string;
	latitude: string;
	locale: string;
	longitude: string;
	photoUrl: string;
	userId: string;
	username: string;
	firstName?: string;
	id?: string;
	networkPropertyChange?: boolean;
	rating?: Rating;
	timeZone?: string;
	useNetwork?: boolean;
}

export interface MatchCreatedActivity {
	actor: Actor;
	foreign_id: string;
	id: string;
	match: Match;
	object: string;
	origin: string;
	target: string;
	time: Date;
	verb: string;
}

export interface Match {
	championship: null;
	club: Club;
	clubInfo: null;
	confirmed: boolean;
	createdAt: number;
	createdBy: string;
	date: number;
	details: MatchDetails;
	disputed: boolean;
	pictures: any[];
	playerA: { [key: string]: null | string };
	playerB: { [key: string]: null | string };
	practice: boolean;
	privateNote: null;
	reservationId: null | string;
	score: MatchScore[];
	uuid: string;
	winnerId: string;
}

export interface MatchDetails {
	duration: number;
	sport: string;
	surface: null | string;
	type: string;
}

export interface MatchScore {
	score: number[];
	uuid: string;
}

export interface MembershipCreatedActivity {
	actor: ClubActor;
	foreign_id: string;
	id: string;
	member: Member;
	object: string;
	origin: string;
	target: string;
	time: Date;
	verb: string;
}

export interface ClubActor {
	id: string;
	collection: string;
	foreign_id: string;
	data: ClubActorData;
	created_at: Date;
	updated_at: Date;
}

export interface ClubActorData {
	address1: string;
	canonicalName: string;
	city: string;
	clubId: string;
	country: string;
	email: string;
	landLinePhone: string;
	latitude: string;
	longitude: string;
	mobilePhone: string;
	name: string;
	photoUrl: string;
	timeZone: string;
	web: string;
	zipCode: string;
}

export interface Member {
	created_at: Date;
	updated_at: Date;
	id: string;
	data: MemberData;
}

export interface PlayerRating {
	ITN: string;
	ITNName: string;
	country: string;
	ratingValue: string;
}

export interface MemberData {
	createdAt: number;
	firstName: string;
	id: string;
	lastName: string;
	latitude: string;
	locale: string;
	longitude: string;
	networkPropertyChange: boolean;
	photoUrl: string;
	timeZone: string;
	useNetwork: boolean;
	userId: string;
	username: string;
	rating: PlayerRating;
}

export interface NewNetworkMemberActivity {
	actor: Member;
	foreign_id: string;
	id: string;
	object: string;
	origin: string;
	target: string;
	time: Date;
	verb: string;
}

export interface ListMatchesResponse {
	responseCode: string;
	pageRows: number;
	pageLimit: number;
	pageCurrent: number;
	data: MatchData[];
}

export interface MatchData {
	uuid: string;
	playerA: MatchPlayer;
	playerB: MatchPlayer;
	createdBy: string;
	championship: null | string;
	reservationId: null | string;
	winnerId: null | string;
	confirmed: boolean;
	disputed: boolean | null;
	score: MatchScore[] | null;
	practice: boolean;
	date: number;
	club: Club;
	clubInfo: ClubInfo;
	details: Details;
	privateNote: null | string;
	pictures: Picture[];
	createdAt: number;
}

export interface Picture {
	id: string;
	thumbnailUrl: string;
	originalUrl: string;
}

export interface Details {
	type: string;
	sport: string;
	surface: null | string;
	duration: number;
}

export interface MatchPlayer {
	userId: string;
	firstName: string;
	lastName: string;
	photoUrl: string;
	city: string;
	country: string;
	age: null | number;
	gender: null | string;
	rating: null | string;
	userRelationship: null | string;
	numberOfFriends: null | number;
	sport: null | string;
	locale: string;
	zone: string;
	coachType: null | string;
}

export interface GetChampionshipsResponse {
	responseCode: string;
	pageRows: number;
	pageLimit: number;
	pageCurrent: number;
	data: Championship[];
}

export interface LeaderboardResponse {
	responseCode: string;
	pageRows: number;
	pageLimit: number;
	pageCurrent: number;
	data: LeaderboardData[];
}

export interface LeaderboardQueryParams {
	gender?: string;
	longitude?: number;
	latitude?: number;
	clubId?: string;
	ageFrom?: number;
	ageTo?: number;
	leagueId?: string;
	playerName?: string;
	watchlist?: boolean;
}

export interface LeaderboardData {
	player: User;
	rank: Rank;
}

export interface Rank {
	rank: null | number;
	points: null | number;
	previousRank: null | number;
	score: null | number;
}

export interface PlacePrediction extends google.maps.places.QueryAutocompletePrediction {
	description: string;
	id: string;
	matched_substrings: MatchedSubstring[];
	place_id: string;
	reference: string;
	structured_formatting: StructuredFormatting;
	terms: Term[];
	types: string[];
}

export interface MatchedSubstring {
	length: number;
	offset: number;
}

export interface StructuredFormatting {
	main_text: string;
	main_text_matched_substrings: MatchedSubstring[];
	secondary_text: string;
}

export interface Term {
	offset: number;
	value: string;
}

export interface ClubAutocompleteResult {
	clubId: string;
	name: string;
	city: string;
	country: string;
	address: string;
	path: string;
	clubSetting: string;
	photoUrl: string;
	type: string;
}

export type LocationSearchResult = {
	type: "club" | "place";
	place?: PlacePrediction;
	club?: ClubAutocompleteResult;
	value: string;
	description?: string;
	country?: string;
	location?: {
		lat: number;
		lng: number;
	};
};

export type NavbarSearchResult = {
	type: "club" | "place" | "player";
	place?: PlacePrediction;
	club?: ClubAutocompleteResult;
	player?: User;
	value: string;
	description?: string;
};

export type MultiSearchResult = {
	type: "club" | "place" | "player";
	place?: PlacePrediction;
	club?: ClubAutocompleteResult;
	player?: User;
	value: string;
	description?: string;
	country?: string;
	location?: {
		lat: number;
		lng: number;
	};
};

export interface Conversation {
	groupType: null | string;
	groupId: null | string;
	conversationId: string;
	numberOfUnreadMessages?: number;
	lastMessages: Message[];
	partners: MessagePartner[];
}

export interface Message {
	preview: string;
	senderId: string;
	messageId: string;
	readBy: string[];
	text: MessageText;
	bot: boolean;
	createdAt: Date;
	updatedAt: Date;
	conversationId: string;
}

export interface MessageText {
	payload: Payload;
	messageType: MessageType;
	deleted?: boolean;
}

export enum MessageType {
	TEXT = "message",
	SLOT = "slot",
	ACTIVITY = "activity",
	MATCH = "match",
	CHAMPIONSHIP = "championship",
}

export interface Payload {
	messagePayload: MessagePayload;
	slotPayload: any;
	championshipPayload?: Championship;
}

export interface MessagePayload {
	message: string;
}

export interface MessagePartner extends BaseUser {
	username: null | string;
	active: boolean;
}

export interface MessagePartnerHash {
	[key: string]: MessagePartner;
}

export interface ChatConversation {
	conversationId: string;
	numberOfUnreadMessages: number;
	messages: Message[];
	hasMore: boolean;
	partners: MessagePartner[];
	partnersHash: MessagePartnerHash;
	lastUpdate: Date;
	typing: string[];
}

export type Connected<F extends (...args: any) => any> = (...params: Parameters<F>) => ReturnType<ReturnType<F>>;

export interface SelectValue {
	value: string;
	label: string;
}

export type SelectValues = SelectValue[];

export interface Sport {
	active: boolean;
	name: string;
	label: string;
	priority: number;
}

export interface SubscriptionPlansResponse {
	responseCode: string;
	data: SubscriptionPlansData;
}

export interface SubscriptionPlans {
	[key: string]: SubscriptionPlan;
}

export interface SubscriptionPlansData {
	plans: SubscriptionPlans;
}

export interface SubscriptionPlan {
	year: SubscriptionPeriod;
	subscribed: boolean;
	available: boolean;
	month: SubscriptionPeriod;
	product: SubscriptionProduct;
	backgroundCheck: boolean;
	subscription_id?: string;
	subscription?: Subscription;
	cancel_at_period_end?: boolean;
}

export interface Subscription {
	id: string;
	object: string;
	application_fee_percent: null;
	billing_cycle_anchor: number;
	billing_thresholds: null;
	cancel_at: null;
	canceled_at: null;
	collection_method: string;
	created: number;
	current_period_end: number;
	current_period_start: number;
	customer: string;
	days_until_due: null;
	default_payment_method: null;
	default_source: string;
	default_tax_rates: any[];
	discount: null;
	ended_at: null;
	latest_invoice: string;
	livemode: boolean;
	metadata: any[];
	next_pending_invoice_item_invoice: null;
	pending_invoice_item_interval: null;
	pending_setup_intent: null;
	plan: SubscriptionPeriod;
	quantity: number;
	schedule: null;
	start_date: number;
	status: string;
	tax_percent: null;
	trial_end: null;
	trial_start: null;
	subscription_id: string;
	cancel_at_period_end: string;
}

export interface SubscriptionPeriod {
	id: string;
	object: string;
	active: boolean;
	aggregate_usage: null;
	amount: number;
	amount_decimal: string;
	billing_scheme: string;
	created: number;
	currency: string;
	interval: string;
	interval_count: number;
	livemode: boolean;
	metadata: any[] | {};
	nickname: string;
	product: string;
	tiers: null;
	tiers_mode: null;
	transform_usage: null;
	trial_period_days: null;
	usage_type: string;
	name: string;
	subscribed: boolean;
}

export interface SubscriptionProduct {
	id: string;
	object: string;
	active: boolean;
	attributes: any[];
	caption: null;
	created: number;
	deactivate_on: any[];
	description: null;
	images: any[];
	livemode: boolean;
	metadata: SubscriptionMetadata;
	name: string;
	package_dimensions: null;
	shippable: null;
	statement_descriptor: null;
	type: string;
	unit_label: null;
	updated: number;
	url: null;
}

export interface SubscriptionMetadata {
	incompatible_with: string;
	player_flag: string;
	flag_value: string;
	terms_url?: string;
	description_url?: string;
	description_label?: string;
	background_check?: string;
}

export interface MembershipsResponse {
	responseCode: string;
	data: ClubMembershipData[];
}

export interface ClubMembershipData {
	clubInfo: ClubInfo;
	clubDetails: {
		memberStatus: "pending" | "member";
	};
}

export interface NewsletterSettingsResponse {
	responseCode: string;
	data: NewsletterData;
}

export interface NewsletterData {
	newsletter: NewsletterSettings;
}

export interface NewsletterSettings {
	subscribed: boolean;
	url: string;
}

export interface APIResponse<T> {
	responseCode: string;
	data: T;
	pageRows?: number;
	pageLimit?: number;
	pageCurrent?: number;
}

export interface IPAPIResponse {
	ip: string;
	country_name: string;
	country_code: string;
	region_code: string;
	region_name: string;
	city: string;
	zip_code: string;
	latitude: number;
	longitude: number;
	time_zone: string;
}

export type ThunkResult<R> = ThunkAction<R, RootState, undefined, any>;
export type MyThunkDispatch = ThunkDispatch<RootState, undefined, any>;

export interface TimezoneDBResponse {
	status: string;
	message: string;
	countryCode: string;
	countryName: string;
	zoneName: string;
	abbreviation: string;
	gmtOffset: number;
	dst: string;
	zoneStart: number;
	zoneEnd: number;
	nextAbbreviation: string;
	timestamp: number;
	formatted: Date;
}

export interface JWTData {
	exp: number;
	uuid: string;
	userId: string;
	roles: string[];
	firstName: string;
	lastName: string;
	uniqueId: string;
	showOnboarding: 0 | 1;
	acceptTOC: 0 | 1;
	resetPassword: 0 | 1;
	language: string;
	country: string;
	zone: string;
	photoUrl: string;
	iat: string;
}

export interface WaitingReservation {
	available: boolean;
	courtType: string;
	surfaceType: string;
	sport: string;
	club: Club;
	reservation: Reservation;
}

export interface Location {
	latitude: number;
	longitude: number;
	timeZone: string;
	countryCode: string;
	continent: null;
	city: string;
}
